import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import {
  Globe, Linkedin, MapPin, Calendar, TrendingUp, FileText,
  Play, Users, Sparkles, Loader2, ExternalLink, ArrowLeft,
  Briefcase, Target, BarChart3, Clock, ChevronRight, Eye, EyeOff,
  Copy, Check, Share2, Send, Pencil, ArrowRight, Undo2,
  IndianRupee, Zap, MapPinned, Brain, MessageSquarePlus, CheckCircle2, X,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "wouter";
import { RichText } from "@/components/ui/rich-text";

const BASE = import.meta.env.BASE_URL;

interface Company {
  id: number;
  name: string;
  description: string | null;
  sector: string | null;
  stage: string | null;
  website: string | null;
  logoUrl: string | null;
  foundedYear: number | null;
  location: string | null;
  thesis: string | null;
  businessModel: string | null;
  geography: string | null;
  showcaseSlug: string | null;
  showcasePublished: string | null;
}

interface Founder {
  id: number;
  name: string;
  email: string | null;
  role: string | null;
  bio: string | null;
  avatarUrl: string | null;
  linkedinUrl: string | null;
}

interface KPI {
  id: number;
  metric: string;
  value: string;
  unit: string | null;
  period: string | null;
  recordedAt: string;
}

interface Update {
  id: number;
  weekOf: string;
  highlights: string | null;
  challenges: string | null;
  asks: string | null;
  status: string;
}

interface Document {
  id: number;
  title: string;
  category: string | null;
  fileUrl: string;
  summary: string | null;
}

interface MediaAsset {
  id: number;
  title: string;
  type: string;
  url: string;
  thumbnailUrl: string | null;
  description: string | null;
  publishedAt: string | null;
}

interface MeeraIntelData {
  summary?: string;
  traction?: {
    revenue?: string;
    users?: string;
    growth?: string;
    keyMetrics?: string[];
  };
  funding?: {
    totalRaised?: string;
    currentRound?: string;
    valuation?: string;
  };
  fundraising?: {
    ask?: string;
    desiredValuation?: string;
    fundraisingHistory?: string;
    promotersCapital?: string;
    fundUtilization?: Array<{ category: string; percentage: string }>;
  };
  financials?: {
    revenueActual?: string;
    revenueProjected?: string;
    monthlyBurn?: string;
    cashRunway?: string;
    ebitda?: string;
    grossMargin?: string;
    cashInBank?: string;
  };
  geography?: string;
  problemStatement?: string;
  competitiveAdvantage?: string;
  competitiveLandscape?: Array<{ name: string; description?: string }>;
  capTable?: Array<{ name: string; role?: string; equity: string }>;
  productPortfolio?: Array<{ name: string; description?: string; pricing?: string }>;
  distributionModel?: string;
  customerProfile?: string;
  advisors?: Array<{ name: string; role?: string; description?: string; linkedinUrl?: string }>;
  teamSize?: number;
  confidence?: number;
}

interface RiyaState {
  status: "idle" | "searching" | "done" | "error";
  message: string;
  found: number;
  imported: number;
}

const stageBadgeColors: Record<string, string> = {
  pipeline: "bg-gray-100 text-gray-700",
  active: "bg-emerald-100 text-emerald-700",
  graduated: "bg-blue-100 text-blue-700",
  paused: "bg-amber-100 text-amber-700",
  exited: "bg-violet-100 text-violet-700",
};

export default function FounderShowcase({ companyId, cohortSlug }: { companyId: number; cohortSlug: string }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<Company | null>(null);
  const [founders, setFounders] = useState<Founder[]>([]);
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [updates, setUpdates] = useState<Update[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [media, setMedia] = useState<MediaAsset[]>([]);
  const [activeClip, setActiveClip] = useState<MediaAsset | null>(null);
  const [riya, setRiya] = useState<RiyaState>({ status: "idle", message: "", found: 0, imported: 0 });

  const getEmbedUrl = (url: string): string | null => {
    if (url.includes("shubhlabh") || url.includes("/clips/") || url.includes("/c/") || url.includes("/watch/")) {
      return url.replace(/\/(c|clips)\//, "/watch/");
    }
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    return null;
  };

  const isEmbeddableMedia = (m: MediaAsset): boolean => {
    if (!m.url) return false;
    if (m.type === "clip" || m.type === "video") return true;
    return !!getEmbedUrl(m.url);
  };
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [priyaInput, setPriyaInput] = useState("");
  const [priyaPlanId, setPriyaPlanId] = useState<string | null>(null);
  const [hasMeeraIntel, setHasMeeraIntel] = useState(false);
  const [meeraIntel, setMeeraIntel] = useState<MeeraIntelData | null>(null);
  const [meeraSummary, setMeeraSummary] = useState<string | null>(null);
  const [priya, setPriya] = useState<{
    status: "idle" | "thinking" | "preview" | "applying" | "done" | "error" | "clarify";
    message: string;
    operations?: Array<{
      entity: string;
      entityId: number;
      field: string;
      oldValue: string | null;
      newValue: string;
      explanation: string;
    }>;
  }>({ status: "idle", message: "" });

  const [intelInput, setIntelInput] = useState("");
  const [intelFeed, setIntelFeed] = useState<any[]>([]);
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelPending, setIntelPending] = useState<{ feedId: number; operations: any[]; meeraAnalysis: string } | null>(null);

  const fetchShowcase = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/showcase`);
      if (res.ok) {
        const data = await res.json();
        setCompany(data.company);
        setFounders(data.founders);
        setKpis(data.kpis);
        setUpdates(data.updates);
        setDocuments(data.documents);
        setMedia(data.media);
        if (data.meeraIntel) {
          setMeeraIntel(data.meeraIntel);
          setHasMeeraIntel(true);
        }
        if (data.meeraSummary) setMeeraSummary(data.meeraSummary);
      }
    } catch {}
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchShowcase(); }, [fetchShowcase]);

  const fetchIntelFeed = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/intel-feed`);
      if (res.ok) setIntelFeed(await res.json());
    } catch {}
  }, [companyId]);

  useEffect(() => { fetchIntelFeed(); }, [fetchIntelFeed]);

  const [intelPollingId, setIntelPollingId] = useState<number | null>(null);

  useEffect(() => {
    if (!intelPollingId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${BASE}api/companies/${companyId}/intel-feed`);
        if (!res.ok) return;
        const entries = await res.json();
        setIntelFeed(entries);
        const entry = entries.find((e: any) => e.id === intelPollingId);
        if (entry && entry.status !== "processing") {
          clearInterval(interval);
          setIntelPollingId(null);
          setIntelLoading(false);

          if (entry.status === "proposed" && entry.proposedChanges?.length > 0) {
            setIntelPending({
              feedId: entry.id,
              operations: entry.proposedChanges,
              meeraAnalysis: entry.meeraAnalysis,
            });
          } else {
            toast({ title: "Meera's verdict", description: entry.meeraAnalysis || "No actionable updates found." });
          }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [intelPollingId, companyId]);

  const handleFeedMeera = async () => {
    const text = intelInput.trim();
    if (!text) return;
    setIntelLoading(true);
    setIntelInput("");

    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/feed-meera`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });

      if (res.ok) {
        const data = await res.json();
        setIntelPollingId(data.entry.id);
        fetchIntelFeed();
      } else {
        const err = await res.json();
        toast({ title: "Couldn't process intel", description: err.error, variant: "destructive" });
        setIntelLoading(false);
      }
    } catch {
      toast({ title: "Network error", description: "Couldn't reach Meera.", variant: "destructive" });
      setIntelLoading(false);
    }
  };

  const handleApproveIntel = async () => {
    if (!intelPending) return;
    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/intel-feed/${intelPending.feedId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        toast({ title: `Applied ${data.appliedCount} update${data.appliedCount > 1 ? "s" : ""}!` });
        setIntelPending(null);
        fetchShowcase();
        fetchIntelFeed();
      }
    } catch {
      toast({ title: "Failed to apply", variant: "destructive" });
    }
  };

  const handleRejectIntel = async () => {
    if (!intelPending) return;
    try {
      await fetch(`${BASE}api/companies/${companyId}/intel-feed/${intelPending.feedId}/reject`, {
        method: "POST",
      });
      setIntelPending(null);
      fetchIntelFeed();
    } catch {}
  };

  const handleEnrichWithMeera = async () => {
    setPriya({ status: "thinking", message: "Let me review Meera's document analysis and find improvements for this showcase..." });
    setPriyaInput("");

    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/enrich-showcase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (res.ok) {
        const data = await res.json();
        const plan = data.plan;
        if (data.planId) setPriyaPlanId(data.planId);

        if (plan.operations?.length > 0) {
          setPriya({
            status: "preview",
            message: plan.summary,
            operations: plan.operations,
          });
        } else {
          setPriya({
            status: "done",
            message: "I reviewed Meera's analysis — the showcase already has all the key information. Looks great!",
          });
        }
      } else {
        const err = await res.json();
        setPriya({ status: "error", message: err.error || "Couldn't generate enrichment suggestions." });
      }
    } catch {
      setPriya({ status: "error", message: "Network issue while connecting with Meera's data." });
    }
  };

  const [riyaHashtags, setRiyaHashtags] = useState("");

  const handleFetchMedia = async () => {
    if (!company) return;
    const customTags = riyaHashtags.split(",").map(t => t.trim()).filter(Boolean);
    const allTags = [company.name, ...customTags];

    setRiya({
      status: "searching",
      message: `Searching ShubhLabh for clips about ${allTags.join(", ")}...`,
      found: 0,
      imported: 0,
    });

    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/fetch-media`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hashtags: allTags }),
      });

      if (res.ok) {
        const data = await res.json();
        setRiya({
          status: "done",
          message: data.imported > 0
            ? `Found ${data.found} clips on ShubhLabh! Added ${data.imported} new ones to the showcase.`
            : data.found > 0
              ? `Found ${data.found} clips but they're already in the showcase. All good!`
              : `No clips found for "${company.name}" on ShubhLabh yet. Try adding a custom hashtag.`,
          found: data.found,
          imported: data.imported,
        });
        if (data.imported > 0) {
          toast({ title: `Riya added ${data.imported} clips!` });
          fetchShowcase();
        }
      } else {
        const err = await res.json();
        setRiya({
          status: "error",
          message: `Couldn't connect to ShubhLabh right now. ${err.error || "Try again in a moment."}`,
          found: 0,
          imported: 0,
        });
      }
    } catch {
      setRiya({
        status: "error",
        message: "Network issue — couldn't reach ShubhLabh. I'll try again when you're ready.",
        found: 0,
        imported: 0,
      });
    }
  };

  const handlePublish = async () => {
    if (!company) return;
    setPublishing(true);
    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/publish-showcase`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast({ title: "Showcase published!", description: `Public link is now live.` });
        fetchShowcase();
      }
    } catch {}
    setPublishing(false);
  };

  const handleUnpublish = async () => {
    if (!company) return;
    setPublishing(true);
    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/unpublish-showcase`, { method: "POST" });
      if (res.ok) {
        toast({ title: "Showcase unpublished", description: "The public link is now inactive." });
        fetchShowcase();
      }
    } catch {}
    setPublishing(false);
  };

  const publicUrl = company?.showcaseSlug
    ? `${window.location.origin}${BASE}showcase/${company.showcaseSlug}`
    : null;

  const copyLink = () => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePriyaSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const instruction = priyaInput.trim();
    if (!instruction) return;

    setPriya({ status: "thinking", message: `Let me understand what you'd like to change...` });

    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/showcase-edit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instruction, autoApply: false }),
      });

      if (res.ok) {
        const data = await res.json();
        const plan = data.plan;
        if (data.planId) setPriyaPlanId(data.planId);

        if (!plan.understood && plan.clarification) {
          setPriya({ status: "clarify", message: plan.clarification });
        } else if (plan.operations?.length > 0) {
          setPriya({
            status: "preview",
            message: plan.summary,
            operations: plan.operations,
          });
        } else {
          setPriya({ status: "clarify", message: plan.summary || "I couldn't figure out what to change. Could you be more specific?" });
        }
      } else {
        const err = await res.json();
        setPriya({ status: "error", message: err.error || "Something went wrong." });
      }
    } catch {
      setPriya({ status: "error", message: "Network issue — couldn't reach the server." });
    }
  };

  const handlePriyaApply = async () => {
    if (!priyaPlanId) return;
    setPriya((prev) => ({ ...prev, status: "applying", message: "Applying changes now..." }));

    try {
      const res = await fetch(`${BASE}api/companies/${companyId}/showcase-edit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId: priyaPlanId }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.applied && data.appliedCount > 0) {
          setPriya({
            status: "done",
            message: `Done! Applied ${data.appliedCount} change${data.appliedCount > 1 ? "s" : ""}. The showcase is updated.`,
          });
          setPriyaInput("");
          fetchShowcase();
        } else {
          setPriya({ status: "error", message: "Couldn't apply the changes. Please try again." });
        }
      } else {
        setPriya({ status: "error", message: "Failed to apply changes." });
      }
    } catch {
      setPriya({ status: "error", message: "Network issue while applying." });
    }
  };

  const handlePriyaDismiss = () => {
    setPriya({ status: "idle", message: "" });
    setPriyaInput("");
    setPriyaPlanId(null);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-64 rounded-2xl" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-12 text-center">
        <p className="text-lg font-serif font-bold text-destructive">Company not found</p>
      </div>
    );
  }

  const latestKpis = kpis.reduce((acc, kpi) => {
    if (!acc[kpi.metric] || new Date(kpi.recordedAt) > new Date(acc[kpi.metric].recordedAt)) {
      acc[kpi.metric] = kpi;
    }
    return acc;
  }, {} as Record<string, KPI>);
  const kpiList = Object.values(latestKpis).slice(0, 6);

  return (
    <div className="space-y-8 pb-12" data-testid="page-founder-showcase">
      <div className="flex items-center justify-between mb-2">
        <Link href={`/c/${cohortSlug}/companies/${companyId}`}>
          <Button variant="ghost" size="sm" className="text-muted-foreground">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Company
          </Button>
        </Link>

        <div className="flex items-center gap-2">
          {company.showcasePublished === "yes" && publicUrl ? (
            <>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                <Eye className="w-3 h-3 mr-1" /> Live
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={copyLink}
                className="text-xs"
              >
                {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                {copied ? "Copied!" : "Copy Public Link"}
              </Button>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="text-xs">
                  <ExternalLink className="w-3 h-3 mr-1" /> Preview Public Page
                </Button>
              </a>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUnpublish}
                disabled={publishing}
                className="text-xs text-muted-foreground hover:text-destructive"
              >
                <EyeOff className="w-3 h-3 mr-1" /> Unpublish
              </Button>
            </>
          ) : (
            <Button
              onClick={handlePublish}
              disabled={publishing}
              size="sm"
              className="bg-[#FF5C00] hover:bg-[#E55200] text-white"
            >
              {publishing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Share2 className="w-3 h-3 mr-1" />}
              Publish Showcase
            </Button>
          )}
        </div>
      </div>

      <div className="force-dark-surface relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#1A1A1A] via-[#2D1810] to-[#1A1A1A] p-10 text-white">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 30% 50%, #FF5C00 0%, transparent 50%), radial-gradient(circle at 70% 30%, #FF5C00 0%, transparent 40%)"
        }} />
        <div className="relative z-10">
          <div className="flex items-start justify-between">
            <div className="max-w-2xl">
              {company.logoUrl && (
                <img src={company.logoUrl} alt={company.name} className="h-12 mb-4 rounded" />
              )}
              <h1 className="text-4xl font-serif font-bold tracking-tight mb-2">{company.name}</h1>
              <div className="flex items-center gap-3 text-white/70 text-sm mb-4">
                {company.sector && (
                  <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />{company.sector}</span>
                )}
                {company.location && (
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{company.location}</span>
                )}
                {company.foundedYear && (
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Est. {company.foundedYear}</span>
                )}
                {company.stage && (
                  <Badge className={`text-[10px] uppercase ${stageBadgeColors[company.stage] || "bg-white/20 text-white"}`}>
                    {company.stage}
                  </Badge>
                )}
              </div>
              {company.description && (
                <RichText text={company.description} className="text-white/80 text-base leading-relaxed" />
              )}
            </div>
            {company.website && (
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white/80 text-sm transition-colors"
              >
                <Globe className="w-4 h-4" /> Visit Website
              </a>
            )}
          </div>

          {company.thesis && (
            <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-xs uppercase tracking-wider text-[#FF5C00] font-medium mb-1">Investment Thesis</p>
              <RichText text={company.thesis} className="text-sm text-white/80 italic leading-relaxed" />
            </div>
          )}

          {company.businessModel && (
            <div className="mt-3 flex items-center gap-2">
              <Briefcase className="w-3.5 h-3.5 text-white/50" />
              <span className="text-xs text-white/50">Business Model:</span>
              <RichText text={company.businessModel} className="text-xs text-white/70 inline" />
            </div>
          )}
        </div>
      </div>

      <Card className="border-rose-200/50 bg-gradient-to-r from-rose-50/50 to-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-rose-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">P</div>
              <span className="text-sm font-semibold">Priya</span>
              <span className="text-[10px] text-muted-foreground font-mono uppercase">Content Editor</span>
            </div>
            {hasMeeraIntel && priya.status === "idle" && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleEnrichWithMeera}
                className="text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[8px] font-bold">M</div>
                Enrich with Meera's intel
              </Button>
            )}
          </div>

          {priya.status !== "idle" && (
            <div className={`mb-3 flex items-start gap-3 p-3 rounded-lg border-l-[3px] ${
              priya.status === "thinking" || priya.status === "applying" ? "bg-rose-50 border-l-rose-400" :
              priya.status === "preview" ? "bg-amber-50 border-l-amber-400" :
              priya.status === "done" ? "bg-emerald-50 border-l-emerald-400" :
              priya.status === "clarify" ? "bg-blue-50 border-l-blue-400" :
              "bg-red-50 border-l-red-400"
            }`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${
                priya.status === "thinking" || priya.status === "applying" ? "bg-rose-100 text-rose-600" :
                priya.status === "preview" ? "bg-amber-100 text-amber-600" :
                priya.status === "done" ? "bg-emerald-100 text-emerald-600" :
                priya.status === "clarify" ? "bg-blue-100 text-blue-600" :
                "bg-red-100 text-red-600"
              }`}>
                {(priya.status === "thinking" || priya.status === "applying") ? <Loader2 className="w-3 h-3 animate-spin" /> : "P"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground/80 leading-relaxed">{priya.message}</p>

                {priya.status === "preview" && priya.operations && (
                  <div className="mt-2 space-y-1.5">
                    {priya.operations.map((op, i) => (
                      <div key={i} className="text-xs bg-white rounded-md p-2 border border-amber-200">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Pencil className="w-3 h-3 text-amber-600" />
                          <span className="font-medium text-amber-800">{op.explanation}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span className="line-through">{op.oldValue || "(empty)"}</span>
                          <ArrowRight className="w-3 h-3" />
                          <span className="text-foreground font-medium">{op.newValue}</span>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 mt-2">
                      <Button size="sm" onClick={handlePriyaApply} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7">
                        <Check className="w-3 h-3 mr-1" /> Apply Changes
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handlePriyaDismiss} className="text-xs h-7 text-muted-foreground">
                        <Undo2 className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {(priya.status === "done" || priya.status === "error" || priya.status === "clarify") && (
                  <Button size="sm" variant="ghost" onClick={handlePriyaDismiss} className="text-xs h-6 mt-1 text-muted-foreground px-2">
                    Dismiss
                  </Button>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handlePriyaSubmit} className="flex items-center gap-2">
            <Input
              value={priyaInput}
              onChange={(e) => setPriyaInput(e.target.value)}
              placeholder={"Tell Priya what to change, e.g. \"Fix Sarah's LinkedIn\" or \"Update the description\""}

              className="flex-1 text-sm h-9"
              disabled={priya.status === "thinking" || priya.status === "applying"}
            />
            <Button
              type="submit"
              size="sm"
              disabled={!priyaInput.trim() || priya.status === "thinking" || priya.status === "applying"}
              className="bg-rose-500 hover:bg-rose-600 text-white h-9 px-3"
            >
              {priya.status === "thinking" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </CardContent>
      </Card>

      {meeraIntel && (meeraIntel.funding || meeraIntel.traction || meeraIntel.summary || meeraIntel.geography) && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[10px] font-bold">M</div>
            <h2 className="text-lg font-serif font-bold">Meera's Intelligence</h2>
            <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700">From Deck Analysis</Badge>
          </div>

          {(meeraSummary || meeraIntel.summary) && (
            <Card className="mb-4 border-emerald-200/50 bg-gradient-to-r from-emerald-50/30 to-white">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <Brain className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-emerald-600 font-medium mb-1">AI Analysis Summary</p>
                    <RichText text={meeraSummary || meeraIntel.summary!} className="text-sm text-foreground/80 leading-relaxed" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {meeraIntel.funding && (
              <Card className="border-emerald-200/40">
                <CardContent className="p-4">
                  <div className="flex items-center gap-1.5 mb-3">
                    <IndianRupee className="w-4 h-4 text-emerald-600" />
                    <p className="text-xs uppercase tracking-wider text-emerald-600 font-medium">Funding</p>
                  </div>
                  <div className="space-y-2">
                    {meeraIntel.funding.totalRaised && (
                      <div>
                        <p className="text-xl font-bold font-mono">{meeraIntel.funding.totalRaised}</p>
                        <p className="text-[10px] text-muted-foreground">Total Raised</p>
                      </div>
                    )}
                    {meeraIntel.funding.currentRound && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{meeraIntel.funding.currentRound}</Badge>
                        {meeraIntel.funding.valuation && (
                          <span className="text-xs text-muted-foreground">@ {meeraIntel.funding.valuation}</span>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {meeraIntel.traction && (
              <Card className="border-emerald-200/40">
                <CardContent className="p-4">
                  <div className="flex items-center gap-1.5 mb-3">
                    <Zap className="w-4 h-4 text-emerald-600" />
                    <p className="text-xs uppercase tracking-wider text-emerald-600 font-medium">Traction</p>
                  </div>
                  <div className="space-y-1.5">
                    {meeraIntel.traction.revenue && (
                      <p className="text-sm"><span className="font-semibold">Revenue:</span> <span className="text-muted-foreground">{meeraIntel.traction.revenue}</span></p>
                    )}
                    {meeraIntel.traction.users && (
                      <p className="text-sm"><span className="font-semibold">Users:</span> <span className="text-muted-foreground">{meeraIntel.traction.users}</span></p>
                    )}
                    {meeraIntel.traction.growth && (
                      <p className="text-sm"><span className="font-semibold">Growth:</span> <span className="text-muted-foreground">{meeraIntel.traction.growth}</span></p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {meeraIntel.geography && (
              <Card className="border-emerald-200/40">
                <CardContent className="p-4">
                  <div className="flex items-center gap-1.5 mb-3">
                    <MapPinned className="w-4 h-4 text-emerald-600" />
                    <p className="text-xs uppercase tracking-wider text-emerald-600 font-medium">Geography</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{meeraIntel.geography}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {meeraIntel.traction?.keyMetrics && meeraIntel.traction.keyMetrics.length > 0 && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {meeraIntel.traction.keyMetrics.map((metric, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/50 border border-emerald-100">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                  <p className="text-xs text-foreground/70">{metric}</p>
                </div>
              ))}
            </div>
          )}

          {meeraIntel.problemStatement && (
            <div className="mt-4 p-4 rounded-lg bg-amber-50/50 border border-amber-100">
              <p className="text-xs uppercase tracking-wider text-amber-600 font-medium mb-1.5">Problem & Opportunity</p>
              <RichText text={meeraIntel.problemStatement} className="text-sm text-foreground/80 leading-relaxed" />
            </div>
          )}

          {meeraIntel.competitiveAdvantage && (
            <div className="mt-3 p-4 rounded-lg bg-blue-50/50 border border-blue-100">
              <p className="text-xs uppercase tracking-wider text-blue-600 font-medium mb-1.5">Competitive Advantage / MOAT</p>
              <RichText text={meeraIntel.competitiveAdvantage} className="text-sm text-foreground/80 leading-relaxed" />
            </div>
          )}

          {meeraIntel.productPortfolio && meeraIntel.productPortfolio.length > 0 && (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Product Portfolio</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {meeraIntel.productPortfolio.map((p, i) => (
                  <div key={i} className="p-3 rounded-lg border bg-white">
                    <p className="text-xs font-medium">{p.name}</p>
                    {p.description && <p className="text-[11px] text-muted-foreground mt-0.5">{p.description}</p>}
                    {p.pricing && <p className="text-[11px] text-[#FF5C00] font-mono mt-1">{p.pricing}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(meeraIntel.financials || meeraIntel.fundraising) && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {meeraIntel.financials && (
                <div className="p-4 rounded-lg border bg-white">
                  <p className="text-xs uppercase tracking-wider text-emerald-600 font-medium mb-2">Financials</p>
                  <div className="space-y-1.5 text-xs">
                    {meeraIntel.financials.revenueActual && <div className="flex justify-between"><span className="text-muted-foreground">Revenue (Actual)</span><span className="font-medium">{meeraIntel.financials.revenueActual}</span></div>}
                    {meeraIntel.financials.revenueProjected && <div className="flex justify-between"><span className="text-muted-foreground">Revenue (Projected)</span><span className="font-medium">{meeraIntel.financials.revenueProjected}</span></div>}
                    {meeraIntel.financials.grossMargin && <div className="flex justify-between"><span className="text-muted-foreground">Gross Margin</span><span className="font-medium text-emerald-600">{meeraIntel.financials.grossMargin}</span></div>}
                    {meeraIntel.financials.ebitda && <div className="flex justify-between"><span className="text-muted-foreground">EBITDA</span><span className="font-medium">{meeraIntel.financials.ebitda}</span></div>}
                    {meeraIntel.financials.monthlyBurn && <div className="flex justify-between"><span className="text-muted-foreground">Monthly Burn</span><span className="font-medium">{meeraIntel.financials.monthlyBurn}</span></div>}
                    {meeraIntel.financials.cashRunway && <div className="flex justify-between"><span className="text-muted-foreground">Cash Runway</span><span className="font-medium">{meeraIntel.financials.cashRunway}</span></div>}
                    {meeraIntel.financials.cashInBank && <div className="flex justify-between"><span className="text-muted-foreground">Cash in Bank</span><span className="font-medium">{meeraIntel.financials.cashInBank}</span></div>}
                  </div>
                </div>
              )}
              {meeraIntel.fundraising && (
                <div className="p-4 rounded-lg border bg-white">
                  <p className="text-xs uppercase tracking-wider text-[#FF5C00] font-medium mb-2">Fundraise</p>
                  <div className="space-y-1.5 text-xs">
                    {meeraIntel.fundraising.ask && <div className="flex justify-between"><span className="text-muted-foreground">Raising</span><span className="font-bold text-[#FF5C00] text-sm">{meeraIntel.fundraising.ask}</span></div>}
                    {meeraIntel.fundraising.desiredValuation && <div className="flex justify-between"><span className="text-muted-foreground">Target Valuation</span><span className="font-medium">{meeraIntel.fundraising.desiredValuation}</span></div>}
                    {meeraIntel.fundraising.promotersCapital && <div className="flex justify-between"><span className="text-muted-foreground">Promoters' Capital</span><span className="font-medium">{meeraIntel.fundraising.promotersCapital}</span></div>}
                    {meeraIntel.fundraising.fundraisingHistory && <div className="flex justify-between"><span className="text-muted-foreground">Prior Rounds</span><span className="font-medium">{meeraIntel.fundraising.fundraisingHistory}</span></div>}
                  </div>
                  {meeraIntel.fundraising.fundUtilization && meeraIntel.fundraising.fundUtilization.length > 0 && (
                    <div className="mt-3 pt-2 border-t">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Use of Funds</p>
                      {meeraIntel.fundraising.fundUtilization.map((fu, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span className="text-muted-foreground">{fu.category}</span>
                          <span className="font-mono">{fu.percentage}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {meeraIntel.capTable && meeraIntel.capTable.length > 0 && (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Cap Table</p>
              <div className="border rounded-lg overflow-hidden">
                <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-gray-50 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  <span>Shareholder</span><span>Role</span><span className="text-right">Equity</span>
                </div>
                {meeraIntel.capTable.map((row, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 px-3 py-2 border-t text-xs">
                    <span>{row.name}</span><span className="text-muted-foreground">{row.role || "—"}</span><span className="text-right font-mono text-[#FF5C00]">{row.equity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {meeraIntel.competitiveLandscape && meeraIntel.competitiveLandscape.length > 0 && (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Competitive Landscape</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {meeraIntel.competitiveLandscape.map((c, i) => (
                  <div key={i} className="p-3 rounded-lg border bg-white">
                    <p className="text-xs font-medium">{c.name}</p>
                    {c.description && <p className="text-[11px] text-muted-foreground mt-0.5">{c.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(meeraIntel.distributionModel || meeraIntel.customerProfile) && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {meeraIntel.distributionModel && (
                <div className="p-4 rounded-lg border bg-white">
                  <p className="text-xs uppercase tracking-wider text-emerald-600 font-medium mb-1.5">Distribution</p>
                  <RichText text={meeraIntel.distributionModel} className="text-xs text-foreground/80 leading-relaxed" />
                </div>
              )}
              {meeraIntel.customerProfile && (
                <div className="p-4 rounded-lg border bg-white">
                  <p className="text-xs uppercase tracking-wider text-emerald-600 font-medium mb-1.5">Customer Profile</p>
                  <RichText text={meeraIntel.customerProfile} className="text-xs text-foreground/80 leading-relaxed" />
                </div>
              )}
            </div>
          )}

          {meeraIntel.advisors && meeraIntel.advisors.length > 0 && (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Advisors</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {meeraIntel.advisors.map((a, i) => (
                  <div key={i} className="p-3 rounded-lg border bg-white">
                    <p className="text-xs font-medium">{a.name}</p>
                    {a.role && <p className="text-[11px] text-[#FF5C00]">{a.role}</p>}
                    {a.description && <p className="text-[11px] text-muted-foreground mt-0.5">{a.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Card className="border-emerald-200/50 bg-gradient-to-r from-emerald-50/30 to-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">M</div>
            <span className="text-sm font-semibold">Feed Meera</span>
            <span className="text-[10px] text-muted-foreground font-mono uppercase">Continuous Intelligence</span>
          </div>

          {intelLoading && !intelPending && (
            <div className="mb-3 flex items-start gap-3 p-3 rounded-lg bg-emerald-50 border-l-[3px] border-l-emerald-400">
              <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Loader2 className="w-3 h-3 text-emerald-600 animate-spin" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground/80">Analyzing your intel... I'm comparing it against the current company data and identifying updates.</p>
              </div>
            </div>
          )}

          {intelPending && (
            <div className="mb-3 p-3 rounded-lg bg-amber-50 border-l-[3px] border-l-amber-400">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-emerald-600">M</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground/80 leading-relaxed mb-2">{intelPending.meeraAnalysis}</p>
                  <div className="space-y-1.5">
                    {intelPending.operations.map((op: any, i: number) => (
                      <div key={i} className="text-xs bg-white rounded-md p-2 border border-amber-200">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Pencil className="w-3 h-3 text-amber-600" />
                          <span className="font-medium text-amber-800">{op.explanation}</span>
                        </div>
                        <div className="flex items-start gap-2 text-muted-foreground">
                          <span className="line-through text-xs max-w-[40%] truncate">{op.oldValue || "(empty)"}</span>
                          <ArrowRight className="w-3 h-3 flex-shrink-0 mt-0.5" />
                          <span className="text-foreground font-medium text-xs max-w-[50%] truncate">{op.newValue}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Button size="sm" onClick={handleApproveIntel} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Approve & Apply
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleRejectIntel} className="text-xs h-7 text-muted-foreground">
                      <X className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2">
            <Textarea
              value={intelInput}
              onChange={(e) => setIntelInput(e.target.value)}
              placeholder={"Paste a news article, share updated metrics, or type a note — e.g. \"NovaPay just closed 50 new enterprise customers and revenue hit ₹50L MRR\""}
              className="flex-1 text-sm min-h-[60px] resize-none"
              disabled={intelLoading}
              rows={2}
            />
            <Button
              onClick={handleFeedMeera}
              size="sm"
              disabled={!intelInput.trim() || intelLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3 mt-0.5"
            >
              {intelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>

          {intelFeed.length > 0 && (
            <div className="mt-3 border-t pt-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Intel Log</p>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {intelFeed.map((entry: any) => (
                  <div key={entry.id} className="flex items-start gap-2 text-xs">
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${
                      entry.status === "approved" ? "bg-emerald-400" :
                      entry.status === "rejected" ? "bg-red-400" :
                      entry.status === "proposed" ? "bg-amber-400" :
                      entry.status === "no_changes" ? "bg-gray-300" :
                      "bg-blue-400"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground/70 truncate">{entry.inputText}</p>
                      {entry.meeraAnalysis && (
                        <p className="text-muted-foreground mt-0.5 text-[11px]">{entry.meeraAnalysis}</p>
                      )}
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-muted-foreground/50">{new Date(entry.createdAt).toLocaleDateString()}</span>
                        <Badge variant="outline" className={`text-[8px] ${
                          entry.status === "approved" ? "border-emerald-200 text-emerald-600" :
                          entry.status === "rejected" ? "border-red-200 text-red-600" :
                          entry.status === "proposed" ? "border-amber-200 text-amber-600" :
                          ""
                        }`}>
                          {entry.status === "approved" ? `${entry.appliedCount} applied` :
                           entry.status === "no_changes" ? "no updates needed" :
                           entry.status}
                        </Badge>
                        {entry.status === "proposed" && entry.proposedChanges && (
                          <button
                            className="text-[9px] text-amber-600 hover:text-amber-700 font-medium underline"
                            onClick={() => setIntelPending({
                              feedId: entry.id,
                              operations: entry.proposedChanges,
                              meeraAnalysis: entry.meeraAnalysis || "",
                            })}
                          >
                            Review
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {founders.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-serif font-bold">Founding Team</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {founders.map((founder) => (
              <Card key={founder.id} className="overflow-hidden">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0 text-lg font-bold text-primary">
                      {founder.avatarUrl ? (
                        <img src={founder.avatarUrl} alt={founder.name} className="w-14 h-14 rounded-full object-cover" />
                      ) : (
                        founder.name.charAt(0)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{founder.name}</h3>
                      </div>
                      {founder.role && <p className="text-xs text-primary font-medium">{founder.role}</p>}
                      {founder.bio && <RichText text={founder.bio} className="text-sm text-muted-foreground mt-1 line-clamp-3" />}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {kpiList.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-serif font-bold">Traction Snapshot</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {kpiList.map((kpi) => (
              <Card key={kpi.id} className="border-l-[3px] border-l-primary/30">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{kpi.metric}</p>
                  <p className="text-2xl font-bold font-mono mt-1">
                    {kpi.value}
                    {kpi.unit && <span className="text-sm font-normal text-muted-foreground ml-1">{kpi.unit}</span>}
                  </p>
                  {kpi.period && <p className="text-[10px] text-muted-foreground mt-1">{kpi.period}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Play className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-serif font-bold">Media &amp; Coverage</h2>
            <Badge variant="outline" className="text-[10px]">ShubhLabh</Badge>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={riyaHashtags}
              onChange={(e) => setRiyaHashtags(e.target.value)}
              placeholder="Custom hashtags (comma separated)"
              className="text-xs border rounded-md px-2.5 py-1.5 w-56 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/40"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleFetchMedia}
              disabled={riya.status === "searching"}
              className="gap-1.5"
            >
              {riya.status === "searching" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {riya.status === "searching" ? "Riya is searching..." : "Ask Riya to find clips"}
            </Button>
          </div>
        </div>

        {riya.status !== "idle" && (
          <div className={`mb-4 flex items-start gap-3 p-3 rounded-lg bg-muted/40 border-l-[3px] ${
            riya.status === "searching" ? "border-l-purple-400" :
            riya.status === "done" ? "border-l-emerald-400" : "border-l-red-400"
          }`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
              riya.status === "searching" ? "bg-purple-100 text-purple-600" :
              riya.status === "done" ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"
            }`}>
              {riya.status === "searching" ? <Loader2 className="w-4 h-4 animate-spin" /> : "R"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-xs font-semibold">Riya</span>
                <span className="text-[10px] text-muted-foreground font-mono uppercase">Media Agent</span>
                {riya.status === "searching" && <span className="text-[10px] text-purple-600 font-medium animate-pulse">searching...</span>}
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed">{riya.message}</p>
            </div>
          </div>
        )}

        {media.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {media.map((asset) => {
              const embeddable = isEmbeddableMedia(asset);
              const CardWrapper = embeddable ? "button" : "a";
              const cardProps = embeddable
                ? { onClick: () => setActiveClip(asset), className: "group block rounded-xl overflow-hidden border hover:shadow-lg transition-shadow text-left w-full" }
                : { href: asset.url, target: "_blank", rel: "noopener noreferrer", className: "group block rounded-xl overflow-hidden border hover:shadow-lg transition-shadow" };
              return (
                <CardWrapper key={asset.id} {...cardProps as any}>
                  {asset.thumbnailUrl ? (
                    <div className="relative aspect-video bg-black">
                      <img src={asset.thumbnailUrl} alt={asset.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-5 h-5 text-primary ml-0.5" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                      <Play className="w-8 h-8 text-primary/40" />
                    </div>
                  )}
                  <div className="p-3">
                    <h4 className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">{asset.title}</h4>
                    {asset.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{asset.description}</p>
                    )}
                    {asset.publishedAt && (
                      <p className="text-[10px] text-muted-foreground mt-2">{new Date(asset.publishedAt).toLocaleDateString()}</p>
                    )}
                  </div>
                </CardWrapper>
              );
            })}
          </div>
        ) : riya.status === "idle" ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-purple-600">R</span>
              </div>
              <p className="text-sm text-muted-foreground mb-1">No media clips yet.</p>
              <p className="text-xs text-muted-foreground">Click "Ask Riya to find clips" to search ShubhLabh for relevant content.</p>
            </CardContent>
          </Card>
        ) : null}
      </div>

      {updates.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-serif font-bold">Update Timeline</h2>
          </div>
          <div className="space-y-3">
            {updates.map((update) => (
              <Card key={update.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Week of {new Date(update.weekOf).toLocaleDateString()}</span>
                    <Badge variant="outline" className="text-[10px]">{update.status}</Badge>
                  </div>
                  {update.highlights && (
                    <div className="mb-2">
                      <p className="text-xs text-emerald-600 font-medium uppercase tracking-wider mb-0.5">Highlights</p>
                      <p className="text-sm text-muted-foreground">{update.highlights}</p>
                    </div>
                  )}
                  {update.challenges && (
                    <div className="mb-2">
                      <p className="text-xs text-amber-600 font-medium uppercase tracking-wider mb-0.5">Challenges</p>
                      <p className="text-sm text-muted-foreground">{update.challenges}</p>
                    </div>
                  )}
                  {update.asks && (
                    <div>
                      <p className="text-xs text-blue-600 font-medium uppercase tracking-wider mb-0.5">Asks</p>
                      <p className="text-sm text-muted-foreground">{update.asks}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {documents.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-serif font-bold">Documents</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {documents.map((doc) => (
              <a
                key={doc.id}
                href={doc.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg border hover:border-primary/30 hover:bg-muted/30 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-primary/60" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{doc.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {doc.category && <Badge variant="outline" className="text-[9px]">{doc.category}</Badge>}
                    {doc.summary && <p className="text-xs text-muted-foreground truncate">{doc.summary}</p>}
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary transition-colors flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="text-center pt-4 border-t">
        <p className="text-xs text-muted-foreground">
          Powered by <span className="font-semibold text-primary">Favcy Select</span>
        </p>
      </div>

      {activeClip && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActiveClip(null)}>
          <div className="relative w-full max-w-4xl bg-background rounded-2xl overflow-hidden shadow-2xl border"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{activeClip.title}</p>
                {activeClip.description && <p className="text-xs text-muted-foreground truncate">{activeClip.description}</p>}
              </div>
              <button onClick={() => setActiveClip(null)}
                className="ml-3 p-1.5 rounded-full hover:bg-muted transition-colors flex-shrink-0">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="aspect-video">
              <iframe
                src={getEmbedUrl(activeClip.url || "") || activeClip.url}
                className="w-full h-full border-0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
